/**
 * Who is holding a title right now, said out loud to everybody.
 *
 * The player report asked for recognition that "is displayed to ALL users" — "player x is top dog
 * this hour" — so this is deliberately not a thing about the reader. It names somebody else, on
 * the landing page and again as one line over the arena, and the only thing it asks of the
 * person reading it is that they want to be on it.
 *
 * ## It costs one cached read, and the interval is the reason
 *
 * `leaderboard.champions` runs **no SQL of its own**: every title is the first entry of a board
 * the API process is already caching, so a room full of clients polling it costs one aggregation
 * per board per TTL rather than one per client. That property only survives if nobody polls
 * faster than the cache turns over — hence `LEADERBOARD_CHAMPION_POLL_MS`, six times the
 * narrowest board's TTL, named in `@chompdome/shared` beside the periods so both halves are
 * reading the same number. A backgrounded app polls nothing at all.
 *
 * **What rotates is the reading, not the asking.** One request carries every title; the line
 * cycles between the ones that have a holder on a local timer, so the callout changes far more
 * often than anything is fetched.
 *
 * ## Never in the way
 *
 * It renders `null` until there is something true to say — a quiet hour in which nobody has
 * finished a run is the ordinary state of a fresh deployment, not an error — and the overlay
 * variant takes no pointer events, because it sits over a canvas that is being steered with
 * every pointer move.
 *
 * ## The seam for the overlay settings card
 *
 * There is no overlay show/hide setting in this build. When one lands, it decides whether the
 * *caller* renders this at all: the in-room line is a slot on `Hud` (`champions`), filled by the
 * route, so hiding it is one condition in one place and nothing in this file has to know that a
 * setting exists.
 */

import {
  LEADERBOARD_CHAMPION_METRICS,
  LEADERBOARD_CHAMPION_POLL_MS,
  type LeaderboardPeriod,
} from '@chompdome/shared/constants';
import { useEffect, useState } from 'react';
import { useAppVisible } from '@/hooks/use-app-visible';
import { championHeadline } from '@/lib/leaderboards';
import { trpc } from '@/lib/trpc';

/**
 * How long each title is on screen.
 *
 * Long enough to read a short sentence twice, short enough that all four are seen inside the time
 * somebody spends deciding which room to play in. It is a render timer and never a fetch: the
 * titles it walks arrived in one answer.
 */
export const CHAMPION_ROTATE_MS = 7_000;

/** The titles the callout asks for: the headline metrics, over the hour. */
const CHAMPION_PERIODS: readonly LeaderboardPeriod[] = ['hour'];

export interface ChampionCalloutProps {
  /**
   * `lobby` is a line in the page's own flow; `overlay` is one line over the arena, which takes
   * no pointer events and wears the HUD's own translucency.
   */
  variant?: 'lobby' | 'overlay';
  className?: string;
}

export function ChampionCallout({ variant = 'lobby', className = '' }: ChampionCalloutProps) {
  const visible = useAppVisible();
  const query = trpc.leaderboard.champions.useQuery(
    { metrics: [...LEADERBOARD_CHAMPION_METRICS], periods: [...CHAMPION_PERIODS] },
    {
      // `false` is React Query's "no interval": a phone in a pocket asks for nothing, and picks
      // the timer back up on the visibility change rather than on a schedule.
      refetchInterval: visible ? LEADERBOARD_CHAMPION_POLL_MS : false,
      refetchIntervalInBackground: false,
    },
  );

  // A title nobody holds is not drawn: an hour in which nobody has finished a run says nothing,
  // and "nobody is top dog" is a worse sentence than no sentence.
  const held = (query.data?.titles ?? []).filter((title) => title.champion !== null);

  const index = useRotation(held.length);
  const title = held[index];
  const champion = title?.champion;
  if (title === undefined || champion === null || champion === undefined) return null;

  const headline = championHeadline(title.metric, title.period, champion.displayName);
  const overlay = variant === 'overlay';

  return (
    <div
      data-testid="champion-callout"
      data-champion-metric={title.metric}
      // Deliberately not a live region. It rotates on a timer, and a screen reader interrupted
      // every seven seconds by a leaderboard is a screen reader nobody leaves on.
      aria-live="off"
      className={
        overlay
          ? `pointer-events-none max-w-[12rem] truncate rounded-full bg-card/40 px-2.5 py-1 text-[10px] text-muted-foreground backdrop-blur-sm sm:max-w-[16rem] sm:text-[11px] ${className}`
          : `flex items-baseline justify-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs text-muted-foreground ${className}`
      }
      title={headline}
    >
      <span className={overlay ? '' : 'truncate'}>
        {overlay ? null : (
          <span aria-hidden="true" className="mr-1">
            🏆
          </span>
        )}
        {headline}
      </span>
      {/* The number behind the title, and only where there is room for it: a title of one is a
          different fact from a title of four hundred, and the arena's line has no space to say
          either. */}
      {!overlay && (
        <span className="shrink-0 tabular-nums text-primary">
          {Math.round(champion.value).toLocaleString()}
        </span>
      )}
    </div>
  );
}

/**
 * Which title is showing: `index`, advanced every `CHAMPION_ROTATE_MS` and wrapped at `count`.
 *
 * Restarted whenever the number of held titles changes, so a title appearing or being taken
 * cannot leave the index pointing past the end of the list for a beat.
 */
function useRotation(count: number): number {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    if (count <= 1) return;
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, CHAMPION_ROTATE_MS);
    return () => clearInterval(timer);
  }, [count]);

  return count === 0 ? 0 : index % count;
}
